import React, { useState, useEffect } from 'react';
import {
    Dialog, DialogTitle, DialogContent, DialogActions, Button, TextField, Box
} from '@mui/material';
import { PayrollService } from '../../services/payroll.service';
import { useToast } from '../../context/ToastContext';
import { useTranslation } from 'react-i18next';

interface Props {
    open: boolean;
    onClose: () => void;
    paymentId: string;
    onSuccess: () => void;
}

const MAX_REASON_LENGTH = 500;

const SkipJournalDialog: React.FC<Props> = ({ open, onClose, paymentId, onSuccess }) => {
    const { t } = useTranslation(['payroll', 'common']);
    const { showSuccess, showApiError } = useToast();
    const [reason, setReason] = useState('');
    const [submitting, setSubmitting] = useState(false);

    useEffect(() => {
        if (open) setReason('');
    }, [open]);

    const handleSubmit = async () => {
        if (!reason.trim()) {
            showApiError(new Error('Reason required'), t('skipJournal.toast.reasonRequired'));
            return;
        }
        setSubmitting(true);
        try {
            await PayrollService.skipJournalPost(paymentId, reason.trim());
            showSuccess(t('skipJournal.toast.skipped'));
            onSuccess();
            onClose();
        } catch (error) {
            showApiError(error, t('skipJournal.toast.failed'));
        } finally {
            setSubmitting(false);
        }
    };

    return (
        <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
            <DialogTitle>{t('skipJournal.title')}</DialogTitle>
            <DialogContent dividers>
                <Box sx={{ pt: 1 }}>
                    <TextField
                        label={t('common:fields.reason')}
                        value={reason}
                        onChange={(e) => setReason(e.target.value.slice(0, MAX_REASON_LENGTH))}
                        multiline
                        rows={3}
                        fullWidth
                        required
                        autoFocus
                        inputProps={{ maxLength: MAX_REASON_LENGTH }}
                        helperText={t('skipJournal.helper', { length: reason.length, max: MAX_REASON_LENGTH })}
                    />
                </Box>
            </DialogContent>
            <DialogActions>
                <Button onClick={onClose} disabled={submitting}>{t('common:actions.cancel')}</Button>
                <Button
                    onClick={handleSubmit}
                    variant="contained"
                    color="warning"
                    disabled={submitting || !reason.trim()}
                >
                    {submitting ? t('skipJournal.skipping') : t('shared.skip')}
                </Button>
            </DialogActions>
        </Dialog>
    );
};

export default SkipJournalDialog;
